import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Activity,
  KeyRound,
  Zap,
  Database,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Copy,
  CheckCircle2,
  RefreshCw,
  Globe,
  Layers,
  BarChart3,
  Clock,
} from 'lucide-react'
import { Link } from 'wouter'
import useSWR from 'swr'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { PLATFORM_DOMAIN, D1_WORKER_URL } from '@/lib/constants'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const fetcher = (url: string) => fetch(url).then(r => r.json())

const QUICK_ENDPOINTS = [
  { method: 'POST', path: '/v1/chat/completions', desc: 'OpenAI-compatible chat' },
  { method: 'POST', path: '/v1/completions', desc: 'Text completions' },
  { method: 'GET', path: '/v1/models', desc: 'List available models' },
  { method: 'POST', path: '/v1/embeddings', desc: 'Generate embeddings' },
]

interface MetricCardProps {
  title: string
  value: string
  delta?: string
  trend?: 'up' | 'down'
  icon: React.ElementType
  accent?: 'lava' | 'cyan' | 'success' | 'warning'
  sub?: string
}

function MetricCard({ title, value, delta, trend, icon: Icon, accent = 'lava', sub }: MetricCardProps) {
  const accentClasses = {
    lava: 'bg-lava/10 text-lava border-lava/20',
    cyan: 'bg-cyan-accent/10 text-cyan-accent border-cyan-accent/20',
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/20',
  }
  const iconBg = {
    lava: 'bg-lava/10 text-lava',
    cyan: 'bg-cyan-accent/10 text-cyan-accent',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
  }

  return (
    <Card className="bg-card border-border hover:border-lava/30 transition-colors group">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium truncate">{title}</p>
            <p className="text-2xl font-bold text-foreground mt-1 tabular-nums">{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
            {delta && (
              <div className="flex items-center gap-1 mt-1">
                {trend === 'up' ? (
                  <TrendingUp className="w-3 h-3 text-success" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-destructive" />
                )}
                <span
                  className={`text-[11px] font-medium ${
                    trend === 'up' ? 'text-success' : 'text-destructive'
                  }`}
                >
                  {delta}
                </span>
                <span className="text-[11px] text-muted-foreground">vs last week</span>
              </div>
            )}
          </div>
          <div className={`p-2 rounded-md flex-shrink-0 ${iconBg[accent]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button onClick={copy} className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors">
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

export default function DashboardPage() {
  const [refreshing, setRefreshing] = useState(false)
  const { data: health, mutate: mutateHealth } = useSWR('/api/health', fetcher, { refreshInterval: 30_000 })

  // Stable chart data — seeded once per mount, not on every render
  const chartData = useMemo(() => Array.from({ length: 14 }, (_, i) => ({
    day: DAYS[i],
    requests: 800 + ((i * 1337 + 42) % 4000),
    tokens:  20000 + ((i * 7919 + 13) % 80000),
  })), [])

  const handleRefresh = () => {
    setRefreshing(true)
    mutateHealth()
    setTimeout(() => setRefreshing(false), 1200)
  }

  const d1Status = health?.d1?.status ?? null
  const d1Latency = health?.d1?.latency_ms ?? null

  const baseUrl = `https://${PLATFORM_DOMAIN}/api/gateway`

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-foreground">Platform Overview</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Cloudflare D1 · Workers AI · R2 · KV · AI Gateway
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-xs border-border"
          onClick={handleRefresh}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          title="Total Requests"
          value="84.2K"
          delta="+18.4%"
          trend="up"
          icon={Zap}
          accent="lava"
        />
        <MetricCard
          title="Active API Keys"
          value="12"
          sub="3 expiring soon"
          icon={KeyRound}
          accent="cyan"
        />
        <MetricCard
          title="Avg Latency"
          value="142ms"
          delta="-23ms"
          trend="up"
          icon={Clock}
          accent="success"
        />
        <MetricCard
          title="Tokens Used"
          value="4.1M"
          delta="+9.2%"
          trend="up"
          icon={BarChart3}
          accent="warning"
        />
      </div>

      {/* Chart + Quick Start */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Requests chart */}
        <Card className="lg:col-span-2 bg-card border-border">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold">Request Volume</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Last 14 days</p>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-lava" />
                Requests
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-cyan-accent" />
                Tokens
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0 pb-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="reqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--lava)" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="var(--lava)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="tokGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--cyan-accent)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="var(--cyan-accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    color: 'var(--foreground)',
                  }}
                />
                <Area type="monotone" dataKey="requests" stroke="var(--lava)" strokeWidth={1.5} fill="url(#reqGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Infrastructure status */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Layers className="w-4 h-4 text-lava" />
              Infrastructure
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {[
              {
                name: 'D1 EdgeSQL',
                id: 'lavaflowstreams',
                live: true,
                status: d1Status === 'ok' ? 'healthy' : d1Status === 'unauthorized' ? 'warn' : d1Status ? 'error' : 'checking',
                latency: d1Latency != null ? `${d1Latency}ms` : '—',
              },
              { name: 'Workers AI', id: 'LAVAFLOW_WORKERS_AI', status: 'healthy', latency: '245ms' },
              { name: 'R2 Bucket', id: 'ide-assets', status: 'healthy', latency: '14ms' },
              { name: 'KV Cache', id: 'DEPLOYMENT_CACHE', status: 'healthy', latency: '3ms' },
              { name: 'AI Gateway', id: 'CF AI Gateway', status: 'healthy', latency: '12ms' },
              { name: 'Rate Limiter', id: 'Namespace #7', status: 'healthy', latency: '2ms' },
            ].map((svc) => (
              <div key={svc.id} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                <div>
                  <p className="text-xs font-medium text-foreground">{svc.name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">{svc.id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground font-mono">{svc.latency}</span>
                  <span className={`w-2 h-2 rounded-full ${
                    svc.status === 'healthy' ? 'bg-success animate-pulse' :
                    svc.status === 'warn' ? 'bg-warning' :
                    svc.status === 'error' ? 'bg-destructive' :
                    'bg-muted-foreground animate-pulse'
                  }`} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Quick Start + Endpoints */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Worker endpoint */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Globe className="w-4 h-4 text-cyan-accent" />
              Worker Endpoint
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-md bg-muted/50 border border-border p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 font-medium">REST API Base URL</p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-cyan-accent flex-1 truncate">{D1_WORKER_URL}</code>
                <CopyButton text={`https://${D1_WORKER_URL}`} />
              </div>
            </div>
            <div className="rounded-md bg-muted/50 border border-border p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 font-medium">AI Gateway (OpenAI-compatible)</p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-lava flex-1 truncate">{baseUrl}</code>
                <CopyButton text={baseUrl} />
              </div>
            </div>
            <div className="space-y-1">
              {QUICK_ENDPOINTS.map((ep) => (
                <div key={ep.path} className="flex items-center gap-2.5 py-1.5 px-2 rounded hover:bg-muted/50 transition-colors group">
                  <Badge
                    variant="outline"
                    className={`text-[9px] px-1.5 h-4 font-mono flex-shrink-0 ${
                      ep.method === 'GET'
                        ? 'border-success/40 text-success bg-success/5'
                        : 'border-lava/40 text-lava bg-lava/5'
                    }`}
                  >
                    {ep.method}
                  </Badge>
                  <code className="text-[11px] font-mono text-foreground flex-1">{ep.path}</code>
                  <span className="text-[10px] text-muted-foreground hidden group-hover:block">{ep.desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick integration snippet */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Activity className="w-4 h-4 text-lava" />
                Quick Integration
              </CardTitle>
              <Badge variant="outline" className="text-[9px] px-1.5 h-4 font-mono border-lava/30 text-lava">
                OpenAI SDK
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative rounded-md bg-muted border border-border overflow-hidden">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-muted/80">
                <span className="text-[10px] text-muted-foreground font-mono">typescript</span>
                <CopyButton text={`import OpenAI from 'openai'\n\nconst client = new OpenAI({\n  apiKey: 'lfk_v1_YOUR_KEY',\n  baseURL: '${baseUrl}',\n})\n\nconst stream = await client.chat.completions.create({\n  model: '@cf/meta/llama-3.1-8b-instruct',\n  messages: [{ role: 'user', content: 'Hello!' }],\n  stream: true,\n})\n\nfor await (const chunk of stream) {\n  process.stdout.write(chunk.choices[0]?.delta?.content ?? '')\n}`} />
              </div>
              <pre className="p-3 text-[11px] font-mono overflow-x-auto leading-relaxed">
                <code>
                  <span className="text-muted-foreground">{'// Drop-in OpenAI SDK replacement'}</span>{'\n'}
                  <span className="text-cyan-accent">{'import'}</span>
                  <span className="text-foreground">{' OpenAI '}</span>
                  <span className="text-cyan-accent">{'from'}</span>
                  <span className="text-success">{" 'openai'"}</span>{'\n\n'}
                  <span className="text-cyan-accent">{'const'}</span>
                  <span className="text-foreground">{' client = '}</span>
                  <span className="text-cyan-accent">{'new'}</span>
                  <span className="text-lava">{' OpenAI'}</span>
                  <span className="text-foreground">{'({'}</span>{'\n'}
                  <span className="text-foreground">{'  apiKey: '}</span>
                  <span className="text-success">{"'lfk_v1_YOUR_KEY'"}</span>
                  <span className="text-foreground">{','}</span>{'\n'}
                  <span className="text-foreground">{'  baseURL: '}</span>
                  <span className="text-success">{`'${baseUrl}'`}</span>{'\n'}
                  <span className="text-foreground">{'})'}</span>{'\n\n'}
                  <span className="text-cyan-accent">{'const'}</span>
                  <span className="text-foreground">{' stream = '}</span>
                  <span className="text-cyan-accent">{'await'}</span>
                  <span className="text-foreground">{' client.chat.completions.'}</span>
                  <span className="text-lava">{'create'}</span>
                  <span className="text-foreground">{'({'}</span>{'\n'}
                  <span className="text-foreground">{'  model: '}</span>
                  <span className="text-success">{"'@cf/meta/llama-3.1-8b-instruct'"}</span>
                  <span className="text-foreground">{','}</span>{'\n'}
                  <span className="text-foreground">{'  stream: '}</span>
                  <span className="text-lava">{'true'}</span>{'\n'}
                  <span className="text-foreground">{'})'}</span>
                </code>
              </pre>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                Compatible with any OpenAI SDK client
              </span>
              <Button asChild variant="ghost" size="sm" className="gap-1 text-xs h-7 text-lava hover:text-lava hover:bg-lava/10">
                <Link href="/dashboard/docs">
                  Full docs <ArrowRight className="w-3 h-3" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
